import type { Rule } from '@dx-shared/rule-engine';
import { RuleEngine } from '@dx-shared/rule-engine';
import type { QueryClient } from '@tanstack/react-query';
import { serverSideGetRuleTranslateUrlsFeatureConfigQuery } from '../generated/queries';
import type { SupportedLanguage } from './types';

export type TranslatedUrlProps = {
  /** path of the url to evaluate in the RuleEngine. */
  path: string;
  /** Langauge to be used for evaluating whether the `path` is translated or not. */
  locale: SupportedLanguage;
  /** The default language. if the `locale` and the `defaultLocale` are the same returns true. */
  defaultLocale?: SupportedLanguage;
  /** react-query client used to perform the query to get the conditions */
  queryClient: QueryClient;
};

export type LegacyTranslatedUrlProps = Omit<TranslatedUrlProps, 'queryClient'> & {
  endpoint: string;
  appName: string;
};

/**
 * Determines if a url has been translated. This determination is performed by the [RuleEngine](https://gitlab.svc-m.hhc.hilton.com/pac/hiw/dx-shared/-/tree/develop/packages/rule-engine) and is run with two facts:
 * - url - @see path
 * - language - @see locale
 *
 * @returns boolean - whether the `path` has been translated for `locale`
 */
export async function isTranslatedUrl({
  path,
  locale,
  queryClient,
  defaultLocale = 'en',
}: TranslatedUrlProps) {
  if (locale === defaultLocale) {
    return true;
  }
  // Prevent errant 404s on client-side transitions. See https://jira.hilton.com/confluence/x/CYiECg for details
  if (/^\/_next\/data\//.test(path)) {
    return true;
  }

  const { featureConfigs } = await serverSideGetRuleTranslateUrlsFeatureConfigQuery(queryClient);
  const engine = new RuleEngine(featureConfigs[0] as Rule);
  const facts = {
    url: path,
    language: locale,
  };
  const isTranslated = engine.run(facts);
  return isTranslated;
}

export default isTranslatedUrl;
